import { BIOMES, type BiomeId } from '../content/biomes';
import {
  COSMETICS,
  CRITTER_TAG_COSMETIC,
  FINDABLE_COSMETICS,
  zoneCosmetic,
  CLEARED_COINS,
  CLEARED_COSMETIC,
  GRAND_COINS,
  GRAND_COSMETIC,
  YARDS_COINS,
  YARDS_COSMETIC,
} from '../content/cosmetics';
import type { CritterKind } from '../sim/critters';
import { mowerNeedsFullGame } from './unlock';
import { MOWER_DEFS, MOWER_IDS, type MowerDef, type MowerId } from '../content/mowers';
import { TOOL_BY_ID, TOOLS, type ToolDef, type ToolId, type ToolUpgrade } from '../content/tools';
import { hashString32 } from '../sim/rng';
import type { ChallengeId } from '../content/challenges';
import { STAR, STAR_BITS, type Save, type UpgradeLevels } from './save/schema';
import type { TurnRuleSpec } from '../sim/turnRules';
import { BOOST_BY_ID, MAX_BOOST_STOCK } from '../content/boosts';
import type { BoostId } from '../sim/boosts';

/**
 * Economy rules (docs/GDD.md §8). Pure functions over the save; numbers are tuning starting points.
 * Everything is earnable by playing: nothing here requires real money.
 */
export const ECONOMY = {
  coinValue: 1,
  firstClearBonus: (level: number) => 10 + level,
  replayClearBonus: 3,
  newStarBonus: 5,
  trampleCost: 1,
  /** Damaged property: a smashed lawn decoration (a gnome; the price of a tier 1 item). */
  smashCost: 5,
  /**
   * Damaged property by break tier (content BREAK_TIER): a cone 2, a gnome or corner flag 5, a
   * bird bath, bin or flag pin 8, a bench, bush, topiary or urn 12, a ball washer 15.
   */
  smashCosts: [2, 5, 8, 12, 15] as readonly number[],
  /** A garden hose section chewed by the mower. */
  hoseCost: 3,
  /** Bumping into the dog (on top of the lost hit). */
  dogBumpCost: 10,
  /** Damaged property: a broken sprinkler head. */
  sprinklerCost: 8,
  /** A smashed beehive: the beekeeper's loss (on top of the sting). */
  hiveCost: 15,
  /** Continues allowed per attempt at a campaign lawn (each costs double the last). */
  maxContinues: 3,
  /** Par star off the campaign (the Daily, shared lawns): this multiple of the par route. */
  parSlack: 1.1,
  duplicateRareCoins: 25,
  dailyClearCoins: 15,
  dailyStreakBonusPerDay: 2,
  dailyStreakBonusCap: 7,
  /** Endless pays out score / this many coins at the end of a run (on top of what it banked). */
  endlessScorePerCoin: 20,
  /** Each lawn cleared in an endless run banks this much. */
  endlessClearBonus: 5,
  /** And this much more for a new best in the mode. */
  endlessBestBonus: 15,
} as const;

// ── Level results ────────────────────────────────────────────────────────────────────────────

export interface AttemptResult {
  levelId: string;
  /** Campaign level number, if the lawn is part of the campaign. */
  level?: number;
  won: boolean;
  ticks: number;
  /** Autopilot clear time for the mower used, if known. */
  parTicks?: number;
  coinsPicked: number;
  totalCoins: number;
  /** Coins paid by spare pickups (not counted toward the all-coins star). */
  spareCoins?: number;
  trampled: number;
  /** Lawn decorations smashed. */
  smashed?: number;
  /** Lawn items smashed, counted by break tier (index 0..4): the damage bill by size. */
  smashedTiers?: readonly number[];
  /** Beehives smashed. */
  hives?: number;
  /** Garden hose sections chewed. */
  chewed?: number;
  /** Times the mower bumped the dog. */
  dogBumps?: number;
  /** Sprinkler heads broken. */
  sprinklersBroken?: number;
  /** Fresh grass tiles cut this attempt. */
  mowedTiles: number;
  /** Coins from a secret yard's treasure (kept only on a clear). */
  treasure?: number;
  /** The lawn's secret yard was opened this attempt (found, once the lawn is cleared). */
  yardFound?: boolean;
  /** How many secret yards the campaign has (given on campaign lawns: the Secret Garden reward). */
  yardTotal?: number;
  /** True if a rare find was picked up this attempt. */
  rarePicked: boolean;
  /** The attempt was continued after failing: no par star, no best time. */
  continued?: boolean;
  /**
   * Boss lawns score their second star on the share of grass mowed instead of time: the target
   * share, and how much was mowed (0..1).
   */
  bossTarget?: number;
  mowedShare?: number;
  /** Lawn art: picture match (0..1) and the match needed for the second and third stars. */
  artMatch?: number;
  artStars?: readonly [number, number];
  /**
   * Mower soccer: goals scored, the goals for the second star, and the share of the pitch mowed
   * (in mowedShare) for the third.
   */
  goals?: number;
  soccerStars?: { goals: number; mowed: number };
  /** The lawn's challenge (the third star) and what it takes to judge it. */
  challenge?: ChallengeId;
  /** Hearts lost to bumps this attempt. */
  heartsLost?: number;
  /** Bumps into critters this attempt. */
  critterBumps?: number;
  /** Tools used this attempt. */
  toolsUsed?: number;
  /** Boosts fired from stock this attempt (the "Use no boosts" challenge). */
  boostsUsed?: number;
  /** The Tuned Engine level in use: the par and gold goals are scaled to it. */
  engine?: number;
  /** Late lawns: the gold time's route for the mower used (the "Gold time" challenge). */
  goldTicks?: number;
  /** The rare find on this lawn, if it isn't the lawn's own (a wandering one). */
  rareItem?: string;
}

export interface Settlement {
  /** Coins added to the wallet. */
  coins: number;
  /** Star bits earned this attempt (0 when lost). */
  stars: number;
  /** Stars earned for the first time. */
  newStars: number;
  firstClear: boolean;
  /** Cosmetic id kept from a rare find, or null. */
  rareItem: string | null;
  /** Coins given instead because the rare item was already owned. */
  rareDuplicateCoins: number;
  /** Itemized coin lines for the results screen. */
  lines: CoinLine[];
  /** This attempt earned the last star in a zone: its All-Stars reward (given once). */
  zoneReward: ZoneReward | null;
  /** This attempt earned the last star of the whole campaign: Stellar Tie-Dye (given once). */
  grandReward: boolean;
  /** This attempt banked the last secret yard: Secret Garden (given once). */
  yardsReward: boolean;
  /** This attempt mowed the last campaign lawn not yet mowed: Finish Line Paint (given once). */
  clearedReward: boolean;
}

export interface ZoneReward {
  zone: BiomeId;
  /** The zone's own cosmetic (never found or sold). */
  cosmetic: string;
  coins: number;
}

/** The All-Stars coin bonus for the n-th zone (0 = the Backyard). */
export const zoneBonus = (zoneIndex: number): number => 100 * (zoneIndex + 1);

/** Whether every star on every lawn of a zone has been earned. */
export function zoneAllStars(levels: Save['levels'], zone: BiomeId): boolean {
  const def = BIOMES.find((b) => b.id === zone);
  if (!def) return false;
  for (let n = def.levels[0]; n <= def.levels[1]; n++) {
    if (countStars(levels[campaignLevelId(n)]?.stars ?? 0) < STAR_BITS.length) return false;
  }
  return true;
}

/**
 * The All-Stars reward this attempt earns, if it brings the last star of its zone: the zone's
 * cosmetic and a coin bonus, each zone only once.
 */
function zoneRewardFor(save: Readonly<Save>, r: AttemptResult, stars: number): ZoneReward | null {
  const n = r.level;
  if (n === undefined || campaignLevelId(n) !== r.levelId) return null;
  const index = BIOMES.findIndex((b) => n >= b.levels[0] && n <= b.levels[1]);
  const zone = BIOMES[index];
  if (!zone || save.zoneRewards.includes(zone.id)) return null;
  const record = save.levels[r.levelId];
  const levels = {
    ...save.levels,
    [r.levelId]: { ...record, stars: (record?.stars ?? 0) | stars },
  };
  if (!zoneAllStars(levels, zone.id)) return null;
  return { zone: zone.id, cosmetic: zoneCosmetic(zone.id).id, coins: zoneBonus(index) };
}

export function starsFor(r: AttemptResult): number {
  if (!r.won) return 0;
  let stars = STAR.clear;
  const unbroken = !r.continued;
  if (r.challenge && challengeMet(r.challenge, r)) stars |= STAR.challenge;
  if (r.artStars) {
    // Lawn art: the judges' scores replace time and coins.
    const match = r.artMatch ?? 0;
    if (match >= r.artStars[0]) stars |= STAR.par;
    if (match >= r.artStars[1]) stars |= STAR.allCoins;
    return stars;
  }
  if (r.soccerStars) {
    // Mower soccer: more goals, and a tidy pitch, replace time and coins.
    if ((r.goals ?? 0) >= r.soccerStars.goals) stars |= STAR.par;
    if ((r.mowedShare ?? 0) >= r.soccerStars.mowed) stars |= STAR.allCoins;
    return stars;
  }
  if (r.bossTarget !== undefined) {
    if (unbroken && (r.mowedShare ?? 0) >= r.bossTarget) stars |= STAR.par;
  } else if (
    unbroken &&
    r.parTicks !== undefined &&
    r.ticks <= parGoal(r.parTicks, r.level, r.engine)
  ) {
    stars |= STAR.par;
  }
  if (r.coinsPicked >= r.totalCoins) stars |= STAR.allCoins;
  return stars;
}

/** Whether a won attempt met the lawn's challenge. */
export function challengeMet(c: ChallengeId, r: AttemptResult): boolean {
  switch (c) {
    case 'flawless':
      return (r.heartsLost ?? 0) === 0;
    case 'oneGo':
      return !r.continued;
    case 'tidy':
      return (
        r.trampled +
          (r.smashed ?? 0) +
          (r.chewed ?? 0) +
          (r.sprinklersBroken ?? 0) +
          (r.hives ?? 0) ===
        0
      );
    case 'gentle':
      return (r.critterBumps ?? 0) === 0;
    case 'noTools':
      return (r.toolsUsed ?? 0) === 0;
    case 'noBoosts':
      return (r.boostsUsed ?? 0) === 0;
    case 'gold':
      return (
        !r.continued && r.goldTicks !== undefined && r.ticks <= goldGoal(r.goldTicks, r.engine)
      );
  }
}

/**
 * Par's slack by zone (owner, 2026-10-31): gentle in the Backyard, none in the Meadows, where par
 * is the best route found. By campaign lawn; off the campaign, `ECONOMY.parSlack`.
 */
export const PAR_SLACK_BY_ZONE = [1.15, 1.12, 1.1, 1.08, 1.06, 1.04, 1.02, 1] as const;

export function parSlack(lawn: number | undefined): number {
  if (lawn === undefined) return ECONOMY.parSlack;
  const zone = Math.min(PAR_SLACK_BY_ZONE.length - 1, Math.max(0, Math.floor((lawn - 1) / 20)));
  return PAR_SLACK_BY_ZONE[zone]!;
}

/** A Tuned Engine level's share of the time to cross a tile. */
export const engineFactor = (engine = 0): number => 1 - ENGINE_BONUS_PER_LEVEL * engine;

/** The par star's time (ticks): the par route, its zone's slack, the engine in use. */
export function parGoal(parTicks: number, lawn: number | undefined, engine = 0): number {
  return Math.round(parTicks * parSlack(lawn) * engineFactor(engine));
}

/**
 * The gold time (ticks): the fastest route found with smashing allowed, at the engine in use
 * (owner, 2026-10-31: the route itself; beating it by a tenth was out of reach of good runs).
 */
export function goldGoal(goldTicks: number, engine = 0): number {
  return Math.round(goldTicks * engineFactor(engine));
}

/** Stars in a record (par, all coins, challenge; clearing a lawn marks it mowed, not a star). */
export const countStars = (bits: number) => STAR_BITS.filter((b) => bits & b).length;

/** The cosmetic a lawn's rare find awards (stable per lawn). */
export function rareItemFor(levelId: string): string {
  // Zone All-Stars rewards (and the full game's thank-you) are never found.
  const pool = FINDABLE_COSMETICS;
  return (pool[hashString32(levelId) % pool.length] as (typeof pool)[number]).id;
}

/** Works out rewards for an attempt without touching the save. */
export function settleAttempt(save: Readonly<Save>, r: AttemptResult): Settlement {
  const lines: Settlement['lines'] = [];
  const picked = (r.coinsPicked + (r.spareCoins ?? 0)) * ECONOMY.coinValue;
  if (!r.won) {
    // A failed attempt pays nothing: the lawn can be replayed and every coin picked up again, so
    // keeping any would make failing a way to farm coins. The results still show what was dropped.
    if (picked > 0) lines.push({ label: 'Coins lost', coins: -picked });
    return {
      coins: 0,
      stars: 0,
      newStars: 0,
      firstClear: false,
      rareItem: null,
      rareDuplicateCoins: 0,
      lines,
      zoneReward: null,
      grandReward: false,
      yardsReward: false,
      clearedReward: false,
    };
  }
  const previous = save.levels[r.levelId]?.stars ?? 0;
  const firstClear = (previous & STAR.clear) === 0;
  const stars = starsFor(r);
  const newStars = countStars(stars & ~previous);

  if (picked > 0) lines.push({ label: 'Coins picked up', coins: picked });
  const clearBonus = firstClear ? ECONOMY.firstClearBonus(r.level ?? 1) : ECONOMY.replayClearBonus;
  lines.push({ label: firstClear ? 'First clear' : 'Clear', coins: clearBonus });
  if (newStars > 0)
    lines.push({ label: `New stars x${newStars}`, coins: newStars * ECONOMY.newStarBonus });
  if ((r.treasure ?? 0) > 0) lines.push({ label: 'Secret yard', coins: r.treasure ?? 0 });
  lines.push(...damageLines(r));

  let rareItem: string | null = null;
  let rareDuplicateCoins = 0;
  // A find picked up in an attempt that was continued has already got away.
  if (r.rarePicked && !r.continued) {
    const item = r.rareItem ?? rareItemFor(r.levelId);
    if (save.rareFinds.includes(item) || save.cosmetics.owned.includes(item)) {
      rareDuplicateCoins = ECONOMY.duplicateRareCoins;
      lines.push({ label: 'Rare find (duplicate)', coins: rareDuplicateCoins });
    } else {
      rareItem = item;
    }
  }
  const zoneReward = zoneRewardFor(save, r, stars);
  if (zoneReward) {
    // (The zone is named on the reveal card; the results line has room for a short label.)
    lines.push({ label: 'All-stars bonus', coins: zoneReward.coins });
  }
  const grandReward = grandRewardFor(save, r, stars);
  if (grandReward) lines.push({ label: 'Every star!', coins: GRAND_COINS });
  const yardsReward = yardsRewardFor(save, r);
  if (yardsReward) lines.push({ label: 'Every yard!', coins: YARDS_COINS });
  const clearedReward = clearedRewardFor(save, r);
  if (clearedReward) lines.push({ label: 'Every lawn!', coins: CLEARED_COINS });
  const coins = Math.max(
    0,
    lines.reduce((sum, l) => sum + l.coins, 0),
  );
  return {
    coins,
    stars,
    newStars,
    firstClear,
    rareItem,
    rareDuplicateCoins,
    lines,
    zoneReward,
    grandReward,
    yardsReward,
    clearedReward,
  };
}

/** Whether every star on every lawn of the campaign has been earned. */
export function campaignAllStars(levels: Save['levels']): boolean {
  return BIOMES.every((zone) => zoneAllStars(levels, zone.id));
}

/**
 * Whether this attempt earns the whole campaign's reward (owner, 2026-10-29): it is a campaign
 * lawn, every star is earned once it counts, and the paint is not owned yet. (A save that already
 * had every star gets it on its next campaign clear.)
 */
function grandRewardFor(save: Readonly<Save>, r: AttemptResult, stars: number): boolean {
  if (save.cosmetics.owned.includes(GRAND_COSMETIC) || !r.won) return false;
  const n = r.level;
  if (n === undefined || campaignLevelId(n) !== r.levelId) return false;
  const record = save.levels[r.levelId];
  return campaignAllStars({
    ...save.levels,
    [r.levelId]: { ...record, stars: (record?.stars ?? 0) | stars },
  });
}

/** Whether every lawn of the campaign has been mowed (won), stars or not. */
export function campaignAllCleared(levels: Save['levels']): boolean {
  return BIOMES.every((zone) => {
    for (let n = zone.levels[0]; n <= zone.levels[1]; n++) {
      if (((levels[campaignLevelId(n)]?.stars ?? 0) & STAR.clear) === 0) return false;
    }
    return true;
  });
}

/**
 * Whether this attempt earns the reward for mowing every campaign lawn (owner, 2026-11-04): a
 * campaign clear that leaves no lawn unmowed, and the paint is not owned yet. (A save that had
 * already mowed every lawn gets it on its next campaign clear.)
 */
function clearedRewardFor(save: Readonly<Save>, r: AttemptResult): boolean {
  if (save.cosmetics.owned.includes(CLEARED_COSMETIC) || !r.won) return false;
  const n = r.level;
  if (n === undefined || campaignLevelId(n) !== r.levelId) return false;
  const record = save.levels[r.levelId];
  return campaignAllCleared({
    ...save.levels,
    [r.levelId]: { ...record, stars: (record?.stars ?? 0) | STAR.clear },
  });
}

/**
 * Whether this attempt earns the secret yards' reward (owner, 2026-10-30): a campaign clear that
 * banks the last yard, and the paint is not owned yet. (A save that had already found every yard
 * gets it on its next campaign clear.)
 */
function yardsRewardFor(save: Readonly<Save>, r: AttemptResult): boolean {
  if (save.cosmetics.owned.includes(YARDS_COSMETIC) || !r.won || !r.yardTotal) return false;
  const found = new Set(save.secretYards);
  if (r.yardFound) found.add(r.levelId);
  return found.size >= r.yardTotal;
}

/** Applies a settlement (and stats) to a save draft. */
export function applySettlement(draft: Save, r: AttemptResult, s: Settlement): void {
  draft.coins += s.coins;
  draft.stats.tilesMowed += r.mowedTiles;
  if (!r.won) return;
  draft.stats.lawnsMowed += 1;
  const record = draft.levels[r.levelId] ?? { stars: 0 };
  record.stars |= s.stars;
  if (!r.continued && (record.bestTicks === undefined || r.ticks < record.bestTicks)) {
    record.bestTicks = r.ticks;
  }
  draft.levels[r.levelId] = record;
  if (r.yardFound && !draft.secretYards.includes(r.levelId)) draft.secretYards.push(r.levelId);
  if (s.zoneReward) {
    const { zone, cosmetic } = s.zoneReward;
    if (!draft.zoneRewards.includes(zone)) draft.zoneRewards.push(zone);
    if (!draft.cosmetics.owned.includes(cosmetic)) draft.cosmetics.owned.push(cosmetic);
  }
  if (s.grandReward && !draft.cosmetics.owned.includes(GRAND_COSMETIC)) {
    draft.cosmetics.owned.push(GRAND_COSMETIC);
  }
  if (s.yardsReward && !draft.cosmetics.owned.includes(YARDS_COSMETIC)) {
    draft.cosmetics.owned.push(YARDS_COSMETIC);
  }
  if (s.clearedReward && !draft.cosmetics.owned.includes(CLEARED_COSMETIC)) {
    draft.cosmetics.owned.push(CLEARED_COSMETIC);
  }
  if (s.rareItem) {
    const item = s.rareItem;
    draft.rareWandering = draft.rareWandering.filter((w) => w.item !== item);
    draft.rareFinds.push(s.rareItem);
    if (!draft.cosmetics.owned.includes(s.rareItem)) draft.cosmetics.owned.push(s.rareItem);
  }
}

/**
 * Price to continue a failed campaign lawn after `used` continues this attempt: the lawn's
 * first-clear reward, doubling each time. Null once the limit is reached.
 */
export function continueCost(level: number, used: number): number | null {
  if (used >= ECONOMY.maxContinues) return null;
  return ECONOMY.firstClearBonus(level) * 2 ** used;
}

/** Pays for a continue. False (and nothing changes) if the wallet is short. */
export function payForContinue(draft: Save, cost: number): boolean {
  if (draft.coins < cost) return false;
  draft.coins -= cost;
  return true;
}

/** Coins for finishing today's Daily (first clear of the day only). */
export function dailyReward(firstClearToday: boolean, streak: number): number {
  if (!firstClearToday) return 0;
  return (
    ECONOMY.dailyClearCoins +
    Math.min(streak, ECONOMY.dailyStreakBonusCap) * ECONOMY.dailyStreakBonusPerDay
  );
}

export function endlessReward(score: number): number {
  return Math.floor(score / ECONOMY.endlessScorePerCoin);
}

/** One line of a results screen's payout. */
export interface CoinLine {
  label: string;
  coins: number;
}

/** The bills for damage done on a lawn (the same on every kind of lawn). */
export function damageLines(r: Readonly<AttemptResult>): CoinLine[] {
  const lines: CoinLine[] = [];
  if (r.trampled > 0)
    lines.push({ label: 'Trampled flowers', coins: -r.trampled * ECONOMY.trampleCost });
  if ((r.smashed ?? 0) > 0) lines.push({ label: 'Smashed decorations', coins: -smashBill(r) });
  if ((r.hives ?? 0) > 0)
    lines.push({ label: 'Smashed beehive', coins: -(r.hives ?? 0) * ECONOMY.hiveCost });
  if ((r.chewed ?? 0) > 0)
    lines.push({ label: 'Chewed hose', coins: -(r.chewed ?? 0) * ECONOMY.hoseCost });
  if ((r.dogBumps ?? 0) > 0)
    lines.push({ label: 'Bumped the dog', coins: -(r.dogBumps ?? 0) * ECONOMY.dogBumpCost });
  if ((r.sprinklersBroken ?? 0) > 0)
    lines.push({
      label: 'Broken sprinkler',
      coins: -(r.sprinklersBroken ?? 0) * ECONOMY.sprinklerCost,
    });
  return lines;
}

/**
 * What one lawn of an endless run banks (owner, 2026-10-04: staged like a campaign lawn, paid out
 * when the run ends): the coins picked up, a bonus when it was cleared, and its damage bills.
 * A lawn that ended the run without being finished banks nothing, as a failed campaign lawn
 * keeps nothing (`finished` false); Rain Chase, one lawn the rain always catches, counts as
 * finished.
 */
export function endlessLawnLines(r: Readonly<AttemptResult>, finished: boolean): CoinLine[] {
  if (!finished) return [];
  const lines: CoinLine[] = [];
  const picked = (r.coinsPicked + (r.spareCoins ?? 0)) * ECONOMY.coinValue;
  if (picked > 0) lines.push({ label: 'Coins picked up', coins: picked });
  if (r.won) lines.push({ label: 'Lawns cleared', coins: ECONOMY.endlessClearBonus });
  lines.push(...damageLines(r));
  return lines;
}

/** Adds a lawn's lines to an endless run's bank: lines with the same label add up. */
export function bankLines(bank: readonly CoinLine[], lines: readonly CoinLine[]): CoinLine[] {
  const out = bank.map((l) => ({ ...l }));
  for (const l of lines) {
    const same = out.find((o) => o.label === l.label);
    if (same) same.coins += l.coins;
    else out.push({ ...l });
  }
  return out;
}

/** Records an endless score on a save draft; true if it is a new best for the mode. */
export function recordEndlessScore(draft: Save, mode: string, score: number): boolean {
  const best = mode === 'lawns' ? draft.endless.best : (draft.endless.bests[mode] ?? 0);
  if (score <= best) return false;
  if (mode === 'lawns') draft.endless.best = score;
  else draft.endless.bests[mode] = score;
  return true;
}

/**
 * Pays out a run left open (the app closed mid-run) on a save draft: its bank, score bonus and any
 * new best, as if it had ended there. Returns the coins paid, or null with no run open.
 */
export function cashOutOpenRun(draft: Save): number | null {
  const run = draft.endless.openRun;
  if (!run) return null;
  delete draft.endless.openRun;
  const best = recordEndlessScore(draft, run.mode, run.score);
  const { coins } = endlessPayout(run.bank, run.score, best);
  draft.coins += coins;
  return coins;
}

/** The net of a set of coin lines (what a bank would pay now; never below zero). */
export const linesTotal = (lines: readonly CoinLine[]): number =>
  Math.max(
    0,
    lines.reduce((sum, l) => sum + l.coins, 0),
  );

/**
 * An endless run's payout when it ends: everything it banked, a bonus by score, and one for a new
 * best in the mode. Bills come off the total, which never goes below zero.
 */
export function endlessPayout(
  bank: readonly CoinLine[],
  score: number,
  newBest: boolean,
): { lines: CoinLine[]; coins: number } {
  const lines = bank.filter((l) => l.coins !== 0).map((l) => ({ ...l }));
  const bonus = endlessReward(score);
  if (bonus > 0) lines.push({ label: 'Score bonus', coins: bonus });
  if (newBest) lines.push({ label: 'New best', coins: ECONOMY.endlessBestBonus });
  return { lines, coins: linesTotal(lines) };
}

// ── Campaign progress ────────────────────────────────────────────────────────────────────────

export const campaignLevelId = (level: number) => `campaign-${String(level).padStart(3, '0')}`;

/** Highest campaign level cleared (0 if none). */
export function campaignProgress(save: Readonly<Save>): number {
  let n = 0;
  while (((save.levels[campaignLevelId(n + 1)]?.stars ?? 0) & STAR.clear) !== 0) n++;
  return n;
}

/** Campaign levels open in order: the next uncleared one is playable. */
export function isCampaignLevelOpen(save: Readonly<Save>, level: number): boolean {
  return level <= campaignProgress(save) + 1;
}

export function totalStars(save: Readonly<Save>): number {
  return Object.values(save.levels).reduce((sum, r) => sum + countStars(r.stars), 0);
}

// ── Mowers ───────────────────────────────────────────────────────────────────────────────────

export type MowerStatus = 'owned' | 'buyable' | 'locked';

export function mowerStatus(save: Readonly<Save>, id: MowerId): MowerStatus {
  if (save.ownedMowers.includes(id)) return 'owned';
  return mowerOpen(save, id) ? 'buyable' : 'locked';
}

/**
 * Whether a mower is open to buy: its lawn cleared, and the full game for the mowers that come
 * with it (the Lawn Tractor on).
 */
export function mowerOpen(save: Readonly<Save>, id: MowerId): boolean {
  const level = MOWER_DEFS[id].unlockLevel;
  return campaignProgress(save) >= level && (save.fullGame || !mowerNeedsFullGame(level));
}

export type PurchaseResult = { ok: true } | { ok: false; reason: 'owned' | 'locked' | 'coins' };

export function canBuyMower(save: Readonly<Save>, id: MowerId): PurchaseResult {
  const status = mowerStatus(save, id);
  if (status === 'owned') return { ok: false, reason: 'owned' };
  if (status === 'locked') return { ok: false, reason: 'locked' };
  if (save.coins < MOWER_DEFS[id].price) return { ok: false, reason: 'coins' };
  return { ok: true };
}

export function buyMower(draft: Save, id: MowerId): PurchaseResult {
  const check = canBuyMower(draft, id);
  if (!check.ok) return check;
  draft.coins -= MOWER_DEFS[id].price;
  draft.ownedMowers.push(id);
  draft.selectedMower = id;
  return check;
}

export const ownedMowerIds = (save: Readonly<Save>): MowerId[] =>
  MOWER_IDS.filter((id) => save.ownedMowers.includes(id));

// ── Tools ────────────────────────────────────────────────────────────────────────────────────

export const SHOP_TOOLS: ToolId[] = TOOLS.filter((t) => t.available).map((t) => t.id);

export function toolStatus(save: Readonly<Save>, id: ToolId): MowerStatus {
  if (save.tools.owned.includes(id)) return 'owned';
  return campaignProgress(save) >= TOOL_BY_ID[id].unlockLevel ? 'buyable' : 'locked';
}

/** Buys a tool and equips it if there's room in the loadout (up to the biggest mower's slots). */
export function buyTool(draft: Save, id: ToolId): PurchaseResult {
  const status = toolStatus(draft, id);
  if (status === 'owned') return { ok: false, reason: 'owned' };
  if (status === 'locked' || !TOOL_BY_ID[id].available) return { ok: false, reason: 'locked' };
  if (draft.coins < TOOL_BY_ID[id].price) return { ok: false, reason: 'coins' };
  draft.coins -= TOOL_BY_ID[id].price;
  draft.tools.owned.push(id);
  if (draft.tools.equipped.length < MAX_TOOL_SLOTS) draft.tools.equipped.push(id);
  return { ok: true };
}

const MAX_TOOL_SLOTS = Math.max(...MOWER_IDS.map((id) => MOWER_DEFS[id].toolSlots));

/** Equips an owned tool (at the front) or unequips it. */
export function toggleEquip(draft: Save, id: ToolId): void {
  if (!draft.tools.owned.includes(id)) return;
  const eq = draft.tools.equipped;
  if (eq.includes(id)) draft.tools.equipped = eq.filter((t) => t !== id);
  else draft.tools.equipped = [id, ...eq].slice(0, MAX_TOOL_SLOTS);
}

/** A tool's upgrade level (0: as bought). */
export function toolLevel(save: Readonly<Save>, id: ToolId): number {
  return save.tools.levels[id] ?? 0;
}

/**
 * A tool's next upgrade (economy pass 2): its price, what it does and the lawn that must be cleared
 * before it is on sale. Null when the tool is not owned or has every level.
 */
export function nextToolUpgrade(
  save: Readonly<Save>,
  id: ToolId,
): (ToolUpgrade & { open: boolean }) | null {
  if (!save.tools.owned.includes(id)) return null;
  const up = TOOL_BY_ID[id].upgrades[toolLevel(save, id)];
  return up ? { ...up, open: campaignProgress(save) >= up.opensAfter } : null;
}

export function buyToolUpgrade(draft: Save, id: ToolId): PurchaseResult {
  const up = nextToolUpgrade(draft, id);
  if (!up) return { ok: false, reason: 'owned' };
  if (!up.open) return { ok: false, reason: 'locked' };
  if (draft.coins < up.price) return { ok: false, reason: 'coins' };
  draft.coins -= up.price;
  draft.tools.levels[id] = toolLevel(draft, id) + 1;
  return { ok: true };
}

/** A tool with its first `level` upgrades applied: what the mower carries. */
export function upgradedTool(def: ToolDef, level: number): ToolDef {
  return def.upgrades.slice(0, level).reduce<ToolDef>(
    (t, up) => ({
      ...t,
      charges: t.charges + (up.charges ?? 0),
      range: t.range + (up.range ?? 0),
      ...(up.sendsHome !== undefined ? { sendsHome: up.sendsHome } : {}),
    }),
    def,
  );
}

/** The tools a mower actually carries: the loadout trimmed to its slots. */
export function loadout(save: Readonly<Save>, mower: MowerId): ToolId[] {
  return save.tools.equipped
    .filter((id) => save.tools.owned.includes(id))
    .slice(0, MOWER_DEFS[mower].toolSlots);
}

// ── Upgrades ─────────────────────────────────────────────────────────────────────────────────

/** The upgrades for sale (the save's levels, less the Tuned Engine's dial-back setting). */
export type UpgradeKind = Exclude<keyof UpgradeLevels, 'engineInUse'>;

/**
 * Per-mower garage upgrades. Each mower gets its own levels; a level's price can grow with the
 * mower's tier (see {@link upgradeCosts}), and an upgrade with no levels for a mower doesn't apply
 * to it (no tank on a reel mower, nothing to steer more freely on a zero-turn).
 */
export const UPGRADES: Record<
  UpgradeKind,
  { name: string; unlockLevel?: number; /** What it does, for its unlock card. */ blurb?: string }
> = {
  tank: { name: 'Bigger Tank' },
  hp: { name: 'Sturdier Frame' },
  blades: { name: 'Sharpened Blades' },
  engine: { name: 'Tuned Engine' },
  steering: { name: 'Power Steering' },
  reverse: { name: 'Reverse Gear' },
  // In the garage once the thorn patches are near (they arrive at lawn 83).
  tires: { name: 'Hardened Tires', unlockLevel: 82 },
  // Economy pass 2 (owner, 2026-10-05): the Cemetery's and the Estate's big goals.
  bumper: { name: 'Bumper', blurb: 'Your first crash on each lawn is free.' },
  reserve: { name: 'Reserve Can', blurb: 'Refills your tank once on each lawn.' },
};

/**
 * What an upgrade does, in a line for the Garage (owner, 2026-10-07: the names alone don't say).
 * Power Steering eases each mower's own turn rule.
 */
export function upgradeBlurb(kind: UpgradeKind, mower: MowerId): string {
  switch (kind) {
    case 'tank':
      return 'Holds more gas.';
    case 'hp':
      return 'Takes one more crash.';
    case 'blades':
      return 'Cuts tall grass in fewer passes.';
    case 'engine':
      return 'A little faster. Dial it back any time.';
    case 'steering':
      return MOWER_DEFS[mower].turnRule.kind === 'turnSpacing'
        ? 'Turns the same way a tile sooner.'
        : 'The pull-back comes back sooner.';
    case 'reverse':
      return 'Swipe back to turn right around.';
    case 'tires':
      return 'Some thorn punctures on each lawn do no harm.';
    case 'bumper':
      return 'Some crashes on each lawn are free: no hit.';
    case 'reserve':
      return 'Refills your tank once on each lawn.';
  }
}

/**
 * Mastery levels (economy pass 2, owner 2026-10-05): later levels of an upgrade open zone by zone,
 * as the campaign reaches them. By upgrade, the level index (0 = the first) and the lawn that must
 * be cleared first; levels not listed are open with the upgrade itself. Comfort only (hits, gas,
 * punctures, crashes): never speed, so par times stay fair.
 */
export const UPGRADE_LEVEL_OPENS: Partial<Record<UpgradeKind, Readonly<Record<number, number>>>> = {
  // Golf (61), the Estate (101), the Stadium (121) and the Meadows (141).
  tank: { 5: 60, 6: 100, 7: 120, 8: 140 },
  hp: { 3: 60, 4: 100, 5: 120, 6: 140 },
  tires: { 3: 140 },
  // The Cemetery (81), then the Stadium (121).
  bumper: { 0: 80, 1: 120 },
  // The Estate (101).
  reserve: { 0: 100 },
};

/**
 * The zones' headline upgrades, by level (0 = the first): each zone's big goal from the Cemetery on,
 * announced on its reveal card. The Bumper (Cemetery, then Stadium), the Reserve Can (Estate) and
 * the Champion Frame (Meadows).
 */
export const HEADLINE_UPGRADES: Partial<Record<UpgradeKind, readonly number[]>> = {
  bumper: [0, 1],
  reserve: [0],
  hp: [6],
};

/** Whether a mower's next level of an upgrade is a headline one. */
export function nextIsHeadline(save: Readonly<Save>, id: MowerId, kind: UpgradeKind): boolean {
  return HEADLINE_UPGRADES[kind]?.includes(upgradeLevels(save, id)[kind]) ?? false;
}

/**
 * Whether a headline level of this upgrade is on sale for the mower and not bought yet (its next
 * level is a step toward it, or the headline itself).
 */
export function headlineAhead(save: Readonly<Save>, id: MowerId, kind: UpgradeKind): boolean {
  const level = upgradeLevels(save, id)[kind];
  return (HEADLINE_UPGRADES[kind] ?? []).some(
    (h) =>
      h >= level &&
      h < upgradeCosts(id, kind).length &&
      campaignProgress(save) >= upgradeLevelOpensAfter(kind, h),
  );
}

/** The lawn that must be cleared before an upgrade's level (0 = the first) is on sale. */
export function upgradeLevelOpensAfter(kind: UpgradeKind, level: number): number {
  return UPGRADE_LEVEL_OPENS[kind]?.[level] ?? UPGRADES[kind].unlockLevel ?? 0;
}

/** Fuel added per Bigger Tank level, as a fraction of the base tank. */
export const TANK_BONUS_PER_LEVEL = 0.15;
/** Time to cross a tile taken off per Tuned Engine level, as a fraction. */
export const ENGINE_BONUS_PER_LEVEL = 0.08;
/** Tall grass is this high: Sharpened Blades levels stop once a mower's bite reaches it. */
const TALL_GRASS = 3;

/** Price of each level of an upgrade for this mower (empty: it doesn't apply). */
export function upgradeCosts(id: MowerId, kind: UpgradeKind): readonly number[] {
  const m: MowerDef = MOWER_DEFS[id];
  const engine = m.fuelTiles !== undefined;
  const rule = m.turnRule;
  switch (kind) {
    case 'tank':
      return engine ? [30, 60, 90, 160, 250, 350, 550, 800, 1000] : [];
    case 'hp':
      return [80, 160, 320, 450, 700, 1200, 2000];
    case 'blades': {
      // One pass fewer through tall grass per level, while there is a pass to save: the first
      // level is cheap on the reel and dear on big mowers, a second (the reel only) is a splurge.
      const levels = Math.max(0, TALL_GRASS - m.bite);
      return [25 + 35 * m.tier, 450].slice(0, levels);
    }
    case 'engine':
      return engine ? [120 + 60 * m.tier, 260 + 120 * m.tier] : [];
    case 'steering':
      if (rule.kind === 'free90Pullback') return [90, 180];
      if (rule.kind === 'turnSpacing' && rule.sameDirectionMinTiles > 1) return [220];
      return [];
    case 'reverse':
      return (rule.kind === 'free90' || rule.kind === 'turnSpacing') && !rule.canReverse
        ? [150 + 75 * m.tier]
        : [];
    case 'tires':
      return [180, 360, 600, 900];
    case 'bumper':
      return [900, 1500];
    case 'reserve':
      return engine ? [1100] : [];
  }
}

export function upgradeLevels(save: Readonly<Save>, id: MowerId): UpgradeLevels {
  return {
    tank: 0,
    hp: 0,
    blades: 0,
    engine: 0,
    steering: 0,
    reverse: 0,
    tires: 0,
    bumper: 0,
    reserve: 0,
    ...save.upgrades[id],
  };
}

/** Whether an upgrade applies to a mower at all. */
export function upgradeApplies(id: MowerId, kind: UpgradeKind): boolean {
  return upgradeCosts(id, kind).length > 0;
}

/** Whether an upgrade is in the garage yet (some arrive with the hazard they answer). */
export function upgradeOpen(save: Readonly<Save>, kind: UpgradeKind): boolean {
  return campaignProgress(save) >= upgradeLevelOpensAfter(kind, 0);
}

/**
 * When a mower's next level of an upgrade is still to open: the lawn to clear first. Null when it
 * is on sale already, maxed, or does not apply.
 */
export function nextUpgradeOpensAfter(
  save: Readonly<Save>,
  id: MowerId,
  kind: UpgradeKind,
): number | null {
  const level = upgradeLevels(save, id)[kind];
  if (level >= upgradeCosts(id, kind).length) return null;
  const after = upgradeLevelOpensAfter(kind, level);
  return campaignProgress(save) >= after ? null : after;
}

/**
 * Mower mastery: every upgrade the mower can take, bought to its last level (some open only in the
 * last zones). A mastered mower wears its gold Master trim.
 */
export function mowerMastered(save: Readonly<Save>, id: MowerId): boolean {
  if (!save.ownedMowers.includes(id)) return false;
  const ups = upgradeLevels(save, id);
  return (Object.keys(UPGRADES) as UpgradeKind[]).every(
    (k) => ups[k] >= upgradeCosts(id, k).length,
  );
}

/** Cost of the next level of an upgrade, or null if maxed or not applicable. */
export function nextUpgradeCost(
  save: Readonly<Save>,
  id: MowerId,
  kind: UpgradeKind,
): number | null {
  if (!upgradeApplies(id, kind) || !save.ownedMowers.includes(id)) return null;
  const level = upgradeLevels(save, id)[kind];
  if (campaignProgress(save) < upgradeLevelOpensAfter(kind, level)) return null;
  return upgradeCosts(id, kind)[level] ?? null;
}

export function buyUpgrade(draft: Save, id: MowerId, kind: UpgradeKind): boolean {
  const cost = nextUpgradeCost(draft, id, kind);
  if (cost === null || draft.coins < cost) return false;
  draft.coins -= cost;
  const levels = upgradeLevels(draft, id);
  levels[kind] += 1;
  // A new engine level goes straight into use (with every level below it).
  if (kind === 'engine') delete levels.engineInUse;
  draft.upgrades[id] = levels;
  return true;
}

/** Tuned Engine levels in use on a mower: all it has bought, unless dialed back. */
export function engineInUse(save: Readonly<Save>, id: MowerId): number {
  const ups = upgradeLevels(save, id);
  return Math.min(ups.engine, ups.engineInUse ?? ups.engine);
}

/**
 * Sets the Tuned Engine to a level bought (0: stock), so a player who finds the mower too quick
 * can slow it without losing the upgrade. A level not bought is ignored.
 */
export function setEngine(draft: Save, id: MowerId, level: number): void {
  const levels = upgradeLevels(draft, id);
  if (!Number.isInteger(level) || level < 0 || level > levels.engine) return;
  if (level === levels.engine) delete levels.engineInUse;
  else levels.engineInUse = level;
  draft.upgrades[id] = levels;
}

/** The mower as the player has upgraded it: what the sim actually runs. */
export function effectiveMower(save: Readonly<Save>, id: MowerId): MowerDef {
  return upgradedMower(MOWER_DEFS[id], upgradeLevels(save, id));
}

/** A mower with the given upgrade levels applied. */
export function upgradedMower(base: MowerDef, ups: Readonly<UpgradeLevels>): MowerDef {
  const engine = Math.min(ups.engine, ups.engineInUse ?? ups.engine);
  const ticks = Math.max(2, Math.round(base.ticksPerTile * (1 - ENGINE_BONUS_PER_LEVEL * engine)));
  return {
    ...base,
    maxHp: base.maxHp + ups.hp,
    bite: base.bite + ups.blades,
    ticksPerTile: ticks,
    turnRule: upgradedTurnRule(base.turnRule, ups.steering, ups.reverse > 0),
    ...(ups.tires > 0 ? { tireGuard: (base.tireGuard ?? 0) + ups.tires } : {}),
    ...(ups.bumper > 0 ? { bumper: (base.bumper ?? 0) + ups.bumper } : {}),
    ...(ups.reserve > 0 && base.fuelTiles !== undefined
      ? { reserveCan: (base.reserveCan ?? 0) + ups.reserve }
      : {}),
    ...(base.fuelTiles !== undefined
      ? { fuelTiles: Math.round(base.fuelTiles * (1 + TANK_BONUS_PER_LEVEL * ups.tank)) }
      : {}),
  };
}

/**
 * Power Steering: a third off the reel's pull-back recharge per level; the tractor turns the same
 * way again a tile sooner. Reverse Gear: quarter-turn mowers gain a 180 (the tractor keeps its
 * turning radius).
 */
function upgradedTurnRule(rule: TurnRuleSpec, steering: number, reverse: boolean): TurnRuleSpec {
  switch (rule.kind) {
    case 'free90Pullback':
      return { ...rule, cooldownTicks: Math.round((rule.cooldownTicks * (3 - steering)) / 3) };
    case 'turnSpacing':
      return {
        ...rule,
        sameDirectionMinTiles: Math.max(1, rule.sameDirectionMinTiles - steering),
        ...(reverse ? { canReverse: true } : {}),
      };
    case 'free90':
      return reverse ? { kind: 'free90', canReverse: true } : rule;
    case 'freeSpin':
      return rule;
  }
}

/** An upgrade's stat on a mower, compactly: the value, and what it is ("2", "hits"). */
function upgradeStat(kind: UpgradeKind, m: MowerDef): { value: string; unit: string } {
  const r = m.turnRule;
  switch (kind) {
    case 'tank':
      return { value: String(m.fuelTiles ?? 0), unit: 'gas' };
    case 'hp':
      return { value: String(m.maxHp), unit: m.maxHp === 1 ? 'hit' : 'hits' };
    case 'blades': {
      const passes = Math.ceil(TALL_GRASS / m.bite);
      return { value: String(passes), unit: passes === 1 ? 'pass' : 'passes' };
    }
    case 'engine':
      return { value: (60 / m.ticksPerTile).toFixed(1), unit: 'tiles/s' };
    case 'steering':
      if (r.kind === 'free90Pullback')
        return { value: `${Math.round(r.cooldownTicks / 6) / 10}s`, unit: 'pull-back' };
      return {
        value: String(r.kind === 'turnSpacing' ? r.sameDirectionMinTiles : 0),
        unit: 'tile turn',
      };
    case 'tires': {
      const n = m.tireGuard ?? 0;
      return { value: String(n), unit: n === 1 ? 'puncture' : 'punctures' };
    }
    case 'reverse': {
      const can =
        r.kind === 'freeSpin' ||
        ((r.kind === 'turnSpacing' || r.kind === 'free90') && r.canReverse === true);
      return { value: can ? 'yes' : 'no', unit: 'reverse' };
    }
    case 'bumper': {
      const n = m.bumper ?? 0;
      return { value: String(n), unit: n === 1 ? 'free crash' : 'free crashes' };
    }
    case 'reserve': {
      const n = m.reserveCan ?? 0;
      return { value: String(n), unit: n === 1 ? 'refill' : 'refills' };
    }
  }
}

/**
 * What an upgrade does for this mower, in one short line for the garage: "1 > 2 hits" before the
 * next level, "3 hits" when maxed.
 */
export function upgradeLine(
  save: Readonly<Save>,
  id: MowerId,
  kind: UpgradeKind,
): { text: string; maxed: boolean } {
  const ups = upgradeLevels(save, id);
  const max = upgradeCosts(id, kind).length;
  const at = (level: number) =>
    upgradeStat(
      kind,
      upgradedMower(MOWER_DEFS[id], { ...ups, [kind]: level, engineInUse: undefined }),
    );
  if (kind === 'engine' && engineInUse(save, id) < ups.engine) {
    // Dialed back: the speed it runs at, and how much of the tuning is in use.
    const inUse = engineInUse(save, id);
    const now = at(inUse);
    return {
      text: `${now.value} ${now.unit} ${inUse === 0 ? 'stock' : `${inUse} of ${ups.engine}`}`,
      maxed: ups.engine >= max,
    };
  }
  const now = at(ups[kind]);
  if (ups[kind] >= max) return { text: `${now.value} ${now.unit}`, maxed: true };
  const next = at(ups[kind] + 1);
  return { text: `${now.value} > ${next.value} ${next.unit}`, maxed: false };
}

// ── Boosts ───────────────────────────────────────────────────────────────────────────────────

/** What the smashed lawn items cost, by size (or at the gnome's price, for an old record). */
export function smashBill(r: Readonly<AttemptResult>): number {
  if (!r.smashedTiers) return (r.smashed ?? 0) * ECONOMY.smashCost;
  return r.smashedTiers.reduce((sum, n, tier) => sum + n * (ECONOMY.smashCosts[tier] ?? 0), 0);
}

/** What smashing one lawn item of this break tier costs. */
export const smashCostFor = (tier: number): number => ECONOMY.smashCosts[tier] ?? 0;

export function boostStock(save: Readonly<Save>, id: BoostId): number {
  return save.boosts[id] ?? 0;
}

export function boostStatus(save: Readonly<Save>, id: BoostId): 'locked' | 'buyable' | 'full' {
  if (campaignProgress(save) < BOOST_BY_ID[id].unlockLevel) return 'locked';
  return boostStock(save, id) >= MAX_BOOST_STOCK ? 'full' : 'buyable';
}

/** Buys one boost into stock. */
export function buyBoost(draft: Save, id: BoostId): PurchaseResult {
  const status = boostStatus(draft, id);
  if (status === 'locked') return { ok: false, reason: 'locked' };
  if (status === 'full') return { ok: false, reason: 'owned' };
  if (draft.coins < BOOST_BY_ID[id].price) return { ok: false, reason: 'coins' };
  draft.coins -= BOOST_BY_ID[id].price;
  draft.boosts[id] = boostStock(draft, id) + 1;
  return { ok: true };
}

/** Takes one boost out of stock (it was fired in a lawn). */
export function spendBoost(draft: Save, id: BoostId): void {
  draft.boosts[id] = Math.max(0, boostStock(draft, id) - 1);
}

// ── Cosmetics ────────────────────────────────────────────────────────────────────────────────

/**
 * Equipped cosmetics live in `save.cosmetics.equipped`: a paint per mower (key `paint.<mower>`)
 * and one stripe pattern for every lawn (key `stripes`).
 */
const cosmeticKey = (id: string, mower: MowerId): string | null => {
  const slot = COSMETICS.find((c) => c.id === id)?.slot;
  return slot === 'paint' ? `paint.${mower}` : slot === 'stripes' ? 'stripes' : null;
};

/** Squirrels and rabbits: quick to dart off, so tagging one (a bump) is a feat. */
export const TAG_CRITTERS: readonly CritterKind[] = ['squirrel', 'rabbit'];

/**
 * A squirrel or rabbit tagged (bumped; it hops off unharmed): the first one gives the Paw Print
 * Stripes, on any lawn, won or not. True when that happened now.
 */
export function tagCritter(draft: Save, kind: CritterKind): boolean {
  if (!TAG_CRITTERS.includes(kind) || draft.cosmetics.owned.includes(CRITTER_TAG_COSMETIC))
    return false;
  draft.cosmetics.owned.push(CRITTER_TAG_COSMETIC);
  return true;
}

/** The paint a mower wears, or null for its own colors. */
export function equippedPaint(save: Readonly<Save>, mower: MowerId): string | null {
  const id = save.cosmetics.equipped[`paint.${mower}`];
  return id && save.cosmetics.owned.includes(id) ? id : null;
}

/** The stripe pattern mowed lawns show, or null for the standard stripes. */
export function equippedStripes(save: Readonly<Save>): string | null {
  const id = save.cosmetics.equipped.stripes;
  return id && save.cosmetics.owned.includes(id) ? id : null;
}

/** Whether a cosmetic is in use (a paint: on this mower). */
export function isEquipped(save: Readonly<Save>, id: string, mower: MowerId): boolean {
  const key = cosmeticKey(id, mower);
  return key !== null && save.cosmetics.equipped[key] === id;
}

/** Puts an owned cosmetic on (a paint: on this mower), or takes it off again. */
export function toggleCosmetic(draft: Save, id: string, mower: MowerId): void {
  const key = cosmeticKey(id, mower);
  if (!key || !draft.cosmetics.owned.includes(id)) return;
  if (draft.cosmetics.equipped[key] === id) delete draft.cosmetics.equipped[key];
  else draft.cosmetics.equipped[key] = id;
}

/**
 * Puts an owned paint on every mower the player owns (owner, 2026-11-04), or, when every one
 * already wears it, takes it off them all. Loaners are left alone.
 */
export function paintAllMowers(draft: Save, id: string): void {
  if (COSMETICS.find((c) => c.id === id)?.slot !== 'paint' || !draft.cosmetics.owned.includes(id))
    return;
  const mowers = ownedMowerIds(draft);
  const off = mowers.every((m) => draft.cosmetics.equipped[`paint.${m}`] === id);
  for (const m of mowers) {
    if (off) delete draft.cosmetics.equipped[`paint.${m}`];
    else draft.cosmetics.equipped[`paint.${m}`] = id;
  }
}

/** Whether every mower the player owns wears this paint. */
export function paintOnAllMowers(save: Readonly<Save>, id: string): boolean {
  return ownedMowerIds(save).every((m) => save.cosmetics.equipped[`paint.${m}`] === id);
}

// ── Rare finds that get away ─────────────────────────────────────────────────────────────────

/**
 * A rare find got away from campaign lawn `lawn` (an attempt there ended without a clean clear):
 * the lawn's own find is gone from it for good, and the find wanders on to a later lawn. A find
 * already wandering just moves on past this lawn.
 */
export function rareFindEscapes(
  draft: Save,
  find: { levelId: string; lawn: number; item: string; wandering: boolean },
): void {
  if (find.wandering) {
    draft.rareWandering = draft.rareWandering.map((w) =>
      w.item === find.item ? { item: w.item, from: find.lawn } : w,
    );
    return;
  }
  if (!draft.rareEscaped.includes(find.levelId)) draft.rareEscaped.push(find.levelId);
  const owned = draft.rareFinds.includes(find.item) || draft.cosmetics.owned.includes(find.item);
  if (!owned && !draft.rareWandering.some((w) => w.item === find.item)) {
    draft.rareWandering.push({ item: find.item, from: find.lawn });
  }
}

/**
 * Lawn Art: what the three judges hold up for a picture this good (out of 10), one a touch
 * stricter and one a touch kinder than the middle.
 */
export function judgeScores(match: number): number[] {
  const m = match * 10;
  return [m - 0.4, m, m + 0.4].map((v) => Math.max(1, Math.min(10, Math.round(v))));
}
